import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../../auth/AuthContext'
import config from '../../../config'
import type { CreateEmpresaRequest, EmpresaDTO, StorageFile, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { clip } from '../../../utils/inputs'
import { COMPANY_BACK_PATH, errorMessage } from '../common/company-common'

export interface CompanyFormState {
    razonSocial: string
    direccion: string
    tel1: string
    tel2: string
    correo1: string
    correo2: string
    facebook: string
    youtube: string
    tiktok: string
    twitter: string
    web: string
    logoUrl: string
}

export interface FormMessage {
    type: 'error' | 'success'
    message: string
}

export const LOGO_ACCEPT = '.jpg,.jpeg,.png'

const LOAD_ERROR = 'No se pudo cargar la empresa.'
const SAVE_ERROR = 'Error al guardar la empresa'
const UPLOAD_ERROR = 'No se pudo subir el logo.'

const emptyState: CompanyFormState = {
    razonSocial: '',
    direccion: '',
    tel1: '',
    tel2: '',
    correo1: '',
    correo2: '',
    facebook: '',
    youtube: '',
    tiktok: '',
    twitter: '',
    web: '',
    logoUrl: '',
}

const toFormState = (body: EmpresaDTO): CompanyFormState => ({
    razonSocial: body.razonSocial || '',
    direccion: body.direccion || '',
    tel1: body.tel1 || '',
    tel2: body.tel2 || '',
    correo1: body.correo1 || '',
    correo2: body.correo2 || '',
    facebook: body.facebook || '',
    youtube: body.youtube || '',
    tiktok: body.tiktok || '',
    twitter: body.twitter || '',
    web: body.web || '',
    logoUrl: body.logoUrl || '',
})

const toRequest = (state: CompanyFormState): CreateEmpresaRequest => ({ ...state })

export const useCompanyFormModel = () => {
    const history = useHistory()
    const location = useLocation()
    const { user } = useAuth()
    const backUrl: string = (location.state as { backUrl?: string } | null)?.backUrl || COMPANY_BACK_PATH

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [logoSrc, setLogoSrc] = useState('')
    const [state, setState] = useState<CompanyFormState>(emptyState)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const response: WrapperResponse<EmpresaDTO | null> | null = await (await APIInvoke.invokeGET('/empresas/default')).json()
                if (!active) return
                if (!response?.ok) throw new Error(response?.message || LOAD_ERROR)
                setState(response.body ? toFormState(response.body) : emptyState)
            } catch (error) {
                console.error(error)
                if (active) setMessage({ type: 'error', message: errorMessage(error, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [])

    useEffect(() => {
        if (!state.logoUrl) {
            setLogoSrc('')
            return undefined
        }
        let active = true
        APIInvoke.resolveStoredFileUrl(state.logoUrl).then((url: string | null | undefined) => {
            if (active) setLogoSrc(url || '')
        })
        return () => { active = false }
    }, [state.logoUrl])

    const patch = (partial: Partial<CompanyFormState>) => setState((current) => ({ ...current, ...partial }))

    const change = {
        razonSocial: (value: string) => patch({ razonSocial: clip(value, 300) }),
        direccion: (value: string) => patch({ direccion: clip(value, 500) }),
        tel1: (value: string) => patch({ tel1: clip(value, 15) }),
        tel2: (value: string) => patch({ tel2: clip(value, 15) }),
        correo1: (value: string) => patch({ correo1: clip(value.trim(), 100) }),
        correo2: (value: string) => patch({ correo2: clip(value.trim(), 100) }),
        facebook: (value: string) => patch({ facebook: clip(value.trim(), 500) }),
        youtube: (value: string) => patch({ youtube: clip(value.trim(), 500) }),
        tiktok: (value: string) => patch({ tiktok: clip(value.trim(), 500) }),
        twitter: (value: string) => patch({ twitter: clip(value.trim(), 500) }),
        web: (value: string) => patch({ web: clip(value.trim(), 500) }),
    }

    const uploadLogo = async (file?: File | null) => {
        if (!file) return
        try {
            setUploading(true)
            setMessage(null)
            const body = new FormData()
            body.append('bucket', config.bucket.name)
            body.append('path', `agentes/${user?.agenteId}/empresa/logo/${file.name}`)
            body.append('file', file)
            const uploaded: WrapperResponse<StorageFile> = await (await APIInvoke.uploadStorage(body)).json()
            if (uploaded.ok !== true || !uploaded.body) throw new Error(uploaded.message || UPLOAD_ERROR)
            patch({ logoUrl: uploaded.body.url })
        } catch (error) {
            console.error(error)
            setMessage({ type: 'error', message: errorMessage(error, UPLOAD_ERROR) })
        } finally {
            setUploading(false)
        }
    }

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        try {
            setSaving(true)
            setMessage(null)
            const response: WrapperResponse<EmpresaDTO> = await (await APIInvoke.invokePOST('/empresas', toRequest(state))).json()
            if (response.ok) {
                setMessage({ type: 'success', message: response.message || 'Empresa guardada correctamente' })
                if (response.body) setState(toFormState(response.body))
            } else {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
            }
        } catch (error) {
            console.error('error al guardar la empresa', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setSaving(false)
        }
    }

    return {
        form: state,
        loading,
        saving,
        uploading,
        busy: loading || saving,
        busyLabel: loading ? 'Cargando empresa…' : 'Guardando empresa…',
        message,
        logoSrc,
        backTo: backUrl,
        change,
        uploadLogo,
        save,
        cancel: () => history.push(backUrl),
    }
}
